import { useState } from "react";
import { Sparkles, Loader2, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const ACTIONS = [
  { key: "grammar", label: "Improve Grammar" },
  { key: "concise", label: "Make Concise" },
  { key: "headline", label: "Suggest Headlines" },
  { key: "reformat", label: "Reformat for Social" },
  { key: "missing_info", label: "Check Missing Info" },
];

export const AIAssistButton = ({ getText, onApply, testId = "ai-assist" }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(null);
  const [result, setResult] = useState("");
  const [action, setAction] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async (act) => {
    const text = getText();
    if (!text || !text.trim()) {
      toast.error("Add some text first");
      return;
    }
    setLoading(act);
    setResult("");
    setAction(act);
    try {
      const { data } = await api.post("/ai/assist", { action: act, text });
      setResult(data.result);
    } catch (e) {
      toast.error("AI assistant is unavailable right now");
    } finally {
      setLoading(null);
    }
  };

  const canApply = action && !["headline", "missing_info"].includes(action);

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}
        data-testid={`${testId}-open-btn`}
        className="gap-1.5 border-accent/40 text-accent hover:bg-accent hover:text-accent-foreground">
        <Sparkles className="h-4 w-4" /> AI Assist
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-accent" /> AI Writing Assistant
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {ACTIONS.map((a) => (
              <Button key={a.key} type="button" variant={action === a.key ? "default" : "secondary"} size="sm"
                onClick={() => run(a.key)} disabled={!!loading} data-testid={`${testId}-action-${a.key}`}>
                {loading === a.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : a.label}
              </Button>
            ))}
          </div>
          {(loading || result) && (
            <div className="mt-2 rounded-lg border bg-secondary/50 p-4 text-sm">
              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
                </div>
              ) : (
                <p className="whitespace-pre-wrap" data-testid={`${testId}-result`}>{result}</p>
              )}
            </div>
          )}
          {result && !loading && (
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => {
                navigator.clipboard.writeText(result); setCopied(true); setTimeout(() => setCopied(false), 1500);
              }} data-testid={`${testId}-copy-btn`}>
                {copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />} Copy
              </Button>
              {canApply && onApply && (
                <Button type="button" size="sm" onClick={() => { onApply(result); setOpen(false); toast.success("Applied"); }}
                  data-testid={`${testId}-apply-btn`}>
                  Apply to field
                </Button>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground">AI suggestions are drafts. Always review before publishing.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
